import React, { useState, useEffect } from 'react';
    import { motion, AnimatePresence } from 'framer-motion';
    import { supabase } from '../../config/supabase';
    import { useAuth } from '../../context/AuthContext';
    import SafeIcon from '../../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';
    import { format } from 'date-fns';

    const { FiX } = FiIcons;

    const EventFormModal = ({ event, onClose, onSuccess }) => {
        const { user } = useAuth();
        const [formData, setFormData] = useState({
            profile_id: event?.profile_id || '',
            title: event?.title || '',
            description: event?.description || '',
            location: event?.location || '',
            start_datetime: event?.start_datetime ? format(new Date(event.start_datetime), "yyyy-MM-dd'T'HH:mm") : '',
            end_datetime: event?.end_datetime ? format(new Date(event.end_datetime), "yyyy-MM-dd'T'HH:mm") : '',
        });
        const [userProfiles, setUserProfiles] = useState([]);
        const [saving, setSaving] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            const fetchUserProfiles = async () => {
               if (!user) return;
                try {
                    const { data, error } = await supabase.from('profiles').select('id, name').eq('user_id', user.id).eq('status', 'approved');
                    if (error) throw error;
                    setUserProfiles(data || []);
                     if (!event && data.length > 0) {
                        setFormData(prev => ({ ...prev, profile_id: data[0].id }));
                    }
                } catch (err) { setError('Could not load your approved profiles.'); }
            };
            fetchUserProfiles();
        }, [user, event]);

        const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

        const handleSubmit = async (e) => {
            e.preventDefault();
            if (!user) { setError('You must be logged in.'); return; }
            if (!formData.profile_id) { setError('Please select a profile for this listing.'); return; }
            
            setSaving(true);
            setError('');

            try {
                const eventData = { ...formData, user_id: user.id, status: 'pending', start_datetime: formData.start_datetime || null, end_datetime: formData.end_datetime || null };
                const response = event?.id ? await supabase.from('events').update(eventData).eq('id', event.id) : await supabase.from('events').insert([eventData]);
                if (response.error) throw response.error;
                onSuccess();
                onClose();
            } catch (error) {
                setError(error.message || 'Error saving event.');
            } finally {
                setSaving(false);
            }
        };

        return (
            <AnimatePresence>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={onClose}>
                    <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-6"><h2 className="text-2xl font-bold text-gray-900">{event ? 'Edit' : 'Post'} Event</h2><button onClick={onClose} className="text-gray-400 hover:text-gray-600"><SafeIcon icon={FiX} className="text-xl" /></button></div>
                        {userProfiles.length === 0 ? <div className="text-center p-4 border border-yellow-300 bg-yellow-50 rounded-lg"><p className="text-yellow-800">You must have an approved profile to post an event.</p></div>
                        : (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}
                                <div><label className="block text-sm font-medium text-gray-700 mb-1">Associate with Profile</label><select name="profile_id" value={formData.profile_id} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-yellow-500" required><option value="" disabled>Select a profile</option>{userProfiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
                                <div><label className="block text-sm font-medium text-gray-700 mb-1">Event Title</label><input type="text" name="title" value={formData.title} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-yellow-500" required /></div>
                                <div><label className="block text-sm font-medium text-gray-700 mb-1">Description</label><textarea name="description" value={formData.description} onChange={handleChange} rows={4} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-yellow-500" required /></div>
                                <div><label className="block text-sm font-medium text-gray-700 mb-1">Location (Optional)</label><input type="text" name="location" value={formData.location} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-yellow-500" /></div>
                                <div><label className="block text-sm font-medium text-gray-700 mb-1">Start Time (Optional)</label><input type="datetime-local" name="start_datetime" value={formData.start_datetime} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-yellow-500" /></div>
                                <div><label className="block text-sm font-medium text-gray-700 mb-1">End Time (Optional)</label><input type="datetime-local" name="end_datetime" value={formData.end_datetime} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-yellow-500" /></div>
                                <div className="flex justify-end space-x-4 pt-4"><button type="button" onClick={onClose} className="px-4 py-2 text-gray-600">Cancel</button><button type="submit" disabled={saving} className="bg-yellow-500 text-white px-6 py-2 rounded-lg hover:bg-yellow-600 disabled:opacity-50">{saving ? 'Saving...' : 'Submit for Review'}</button></div>
                            </form>
                        )}
                    </motion.div>
                </motion.div>
            </AnimatePresence>
        );
    };

    export default EventFormModal;